import { ChatContextRef, ChatContextRefKindEnum } from 'lib-trend-core';

/**
 * Internal Chat work references — shared presentation/navigation helpers
 * (SPEC-2026-07-internal-chat, T-22 / RF-35 / AC-31).
 *
 * A `contextRef` is a pointer from a chat message to a work record living in
 * the main application database (Attendance / CRM Card / Contact) plus the
 * display snapshot captured at send time. These helpers are pure functions so
 * both the reference card and the picker dialog share one source of truth
 * without depending on each other.
 */

/** Router target of a reference — `router.navigate(commands, { queryParams })`. */
export interface ChatContextRefRoute {
  commands: Array<string>;
  queryParams?: Record<string, string>;
}

/** Remix icon of each reference kind (never mixed with another icon set). */
export function contextRefIcon(kind: ChatContextRefKindEnum): string {
  switch (kind) {
    case ChatContextRefKindEnum.ATTENDANCE: return 'ri-customer-service-2-line';
    case ChatContextRefKindEnum.CARD: return 'ri-price-tag-3-line';
    case ChatContextRefKindEnum.CONTACT: return 'ri-user-3-line';
    default: return 'ri-links-line';
  }
}

/** i18n key of the kind label ("Atendimento" / "Card" / "Contato"). */
export function contextRefKindLabelKey(kind: ChatContextRefKindEnum): string {
  switch (kind) {
    case ChatContextRefKindEnum.ATTENDANCE: return 'chat.contextRef.kindAttendance';
    case ChatContextRefKindEnum.CARD: return 'chat.contextRef.kindCard';
    case ChatContextRefKindEnum.CONTACT: return 'chat.contextRef.kindContact';
    default: return 'chat.contextRef.kindUnknown';
  }
}

/**
 * Deep link of a reference. The TARGET module's own guards/permissions decide
 * whether the user may see the record — the chat card only navigates, it never
 * pre-authorizes and never fetches the referenced record.
 */
export function contextRefRoute(reference: ChatContextRef | null | undefined): ChatContextRefRoute | null {
  const refId = reference?.refId;
  if (!reference || !refId) return null;

  switch (reference.kind) {
    case ChatContextRefKindEnum.ATTENDANCE:
      return { commands: ['/attendance/panel'], queryParams: { id: refId } };
    case ChatContextRefKindEnum.CARD:
      // Query param, not a child route: the kanban opens the card details
      // DIALOG over the board (`/kanban/card/:id` mounted the dialog component
      // as a page — an empty shell — and was removed).
      return { commands: ['/kanban'], queryParams: { card: refId } };
    case ChatContextRefKindEnum.CONTACT:
      return { commands: ['/contact', 'view', refId] };
    default:
      return null;
  }
}
